import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import {
  Lock,
  ExternalLink,
  Plus,
  Folder,
  Calendar,
  Layers,
  MoreVertical,
  Pencil,
  Trash2,
  HelpCircle,
  AlertCircle,
} from "lucide-react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { api } from "../api";
import { useToast } from "../ui/toast";
import { useModal, Modal } from "../ui/modal";
import { useT } from "../i18n";
import { Button, StatusBadge as UiStatusBadge, statusLabel, projectDisplayName } from "../ui";
import ProjectTabs, { ProjectTabId } from "../components/ProjectTabs";
import ProjectChatView from "../components/ProjectChatView";
import ProjectIntake from "../components/ProjectIntake";
import PhaseWizard from "../components/PhaseWizard";
import TaskWizard from "../components/TaskWizard";
import { TaskDrawer } from "../components/TaskDrawer";
import PresentationWizard from "../components/PresentationWizard";
import InstructionsPanel from "../components/InstructionsPanel";
import KnowledgePanel from "../components/KnowledgePanel";
import FleetToggle from "../components/FleetToggle";

interface ProjectView {
  id: string;
  name: string;
  owner: string | null;
  source: "local" | "salesforce";
  sf_url: string | null;
  read_only: boolean;
}

interface TaskView {
  id: string;
  project_id: string | null;
  title: string;
  description: string | null;
  status: string | null;
  start_date: string | null;
  due_date: string | null;
  owner: string | null;
  source: "local" | "salesforce";
  sf_url: string | null;
  read_only: boolean;
  phase_id: string | null;
  needs_human?: boolean;
}

interface PhaseOut {
  id: string;
  project_id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

type PhaseFilter = "all" | "none" | string;

interface PresentationListItem {
  id: string;
  title: string;
  status: string;
  target_slide_count: number | null;
  pptx_object_key: string | null;
  tokens_in: number;
  tokens_out: number;
  created_at: string;
  updated_at: string;
}

type Column = "open" | "in_progress" | "done";
const COL_COLOR: Record<Column, string> = {
  open: "border-accent-300/40 bg-accent-50/30",
  in_progress: "border-warning/40 bg-warning/30",
  done: "border-success/40 bg-success/30",
};

function normalizeStatus(s: string | null): Column {
  if (!s) return "open";
  const l = s.toLowerCase();
  if (l === "done" || l === "closed" || l === "lost") return "done";
  if (l === "in_progress" || l === "ongoing") return "in_progress";
  return "open";
}

export default function ProjectsPage() {
  const { projectId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const modal = useModal();
  const t = useT();
  const initialSrc: "all" | "local" | "salesforce" = (() => {
    const s = search.get("scope");
    if (s === "local") return "local";
    if (s === "sf") return "salesforce";
    return "all";
  })();
  const [projects, setProjects] = useState<ProjectView[]>([]);
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [filter] = useState("");
  const [src, setSrc] = useState<"all" | "local" | "salesforce">(initialSrc);

  useEffect(() => {
    setSrc(initialSrc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.get("scope")]);
  const [scope] = useState<"mine" | "all">(() => {
    return (localStorage.getItem("sb_sf_scope_v1") as "mine" | "all") || "mine";
  });
  // sf_mapping_missing wird vom Backend geliefert, ist in der neuen UI aber
  // ohne sichtbaren Effekt (SF-Sichtbarkeits-Toggle wurde mit Phase 1 entfernt).
  const [, setSfMappingMissing] = useState(false);
  const [newName, setNewName] = useState("");
  const [dragging, setDragging] = useState<TaskView | null>(null);
  // Default-Tab: 'tasks' (Aufgaben). User-Wunsch: Projekt-Klick landet direkt
  // im Aufgaben-Tab, nicht in Chats.
  const [activeTab, setActiveTab] = useState<ProjectTabId>("tasks");
  const [phases, setPhases] = useState<PhaseOut[]>([]);
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>("all");
  const [phaseWizardOpen, setPhaseWizardOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<PhaseOut | null>(null);
  const [taskWizardOpen, setTaskWizardOpen] = useState(false);
  // Karten-Klick oeffnet den vollen TaskDrawer (Feedback-Banner fuer offene
  // ask-Fragen, Activity, Status, Reviews) statt des reinen Edit-Formulars —
  // sonst kann der User eine ask-Rueckfrage auf der Projektseite nicht
  // beantworten (die TaskWizard-Edit-Ansicht hat keinen Banner).
  const [drawerTask, setDrawerTask] = useState<{
    id: string;
    source: "local" | "salesforce";
    sf_url?: string | null;
    read_only?: boolean;
  } | null>(null);
  const [presentations, setPresentations] = useState<PresentationListItem[]>([]);
  const [presentationWizardOpen, setPresentationWizardOpen] = useState(false);
  // Mobile: rechte Rail (Instruktionen/Knowledge) liegt hinter einem Toggle,
  // damit sie den Chat-Composer nicht ueberdeckt. Desktop ignoriert das Flag.
  const [railOpen, setRailOpen] = useState(false);
  // Projekt-Loeschen: dediziertes Modal mit lokalem busy/error-State (NICHT
  // useModal().confirm(), weil der bei Bestaetigung schliesst und keinen
  // In-Modal-Fehler zeigen kann). Bleibt bei Fehler offen, navigiert nur bei 204.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  // Tab-Reset bei Projekt-Wechsel: lokale Projekte starten in "chats",
  // SF-Projekte (kein Chats-Tab) in "tasks".
  useEffect(() => {
    const cur = projects.find((p) => p.id === projectId);
    setActiveTab(cur?.source === "salesforce" ? "tasks" : "chats");
    setRailOpen(false);
  }, [projectId, projects]);

  useEffect(() => {
    localStorage.setItem("sb_sf_scope_v1", scope);
  }, [scope]);

  const loadProjects = async () => {
    const resp = await api.get<{
      projects: ProjectView[];
      scope: "mine" | "all";
      sf_mapping_missing: boolean;
    }>(`/views/projects?scope=${scope}`);
    setProjects(resp.projects);
    setSfMappingMissing(resp.sf_mapping_missing);
  };

  const loadTasks = async (pid: string) =>
    setTasks(
      await api.get<TaskView[]>(`/views/tasks?project_id=${encodeURIComponent(pid)}`)
    );

  const loadPhases = async (pid: string, source: "local" | "salesforce") => {
    if (source !== "local") {
      setPhases([]);
      return;
    }
    try {
      setPhases(await api.get<PhaseOut[]>(`/projects/${encodeURIComponent(pid)}/phases`));
    } catch (e) {
      // Phasen-API noch nicht erreichbar oder kein Zugriff — UI rendert
      // einfach ohne Phasen-Tabs weiter.
      setPhases([]);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [scope]);

  useEffect(() => {
    if (projectId) {
      loadTasks(projectId);
    } else {
      setTasks([]);
      setPhases([]);
    }
    setPhaseFilter("all");
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const cur = projects.find((p) => p.id === projectId);
    if (!cur) return;
    loadPhases(projectId, cur.source);
  }, [projectId, projects]);

  const loadPresentations = async (pid: string) => {
    try {
      setPresentations(
        await api.get<PresentationListItem[]>(`/projects/${pid}/presentations`)
      );
    } catch {
      setPresentations([]);
    }
  };
  useEffect(() => {
    if (!projectId) {
      setPresentations([]);
      return;
    }
    const cur = projects.find((p) => p.id === projectId);
    if (cur?.source === "local") loadPresentations(projectId);
  }, [projectId, projects]);

  const currentProject = projects.find((p) => p.id === projectId);

  const filteredProjects = useMemo(() => {
    const f = filter.toLowerCase();
    return projects.filter(
      (p) =>
        (!f || p.name.toLowerCase().includes(f)) &&
        (src === "all" || p.source === src)
    );
  }, [projects, filter, src]);

  const localProjects = filteredProjects.filter((p) => p.source === "local");

  const createProject = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const p = await api.post<{ id: string }>("/projects", { name: newName });
    setNewName("");
    await loadProjects();
    navigate(`/p/${p.id}`);
    toast.ok(t.projects.projektAngelegt);
  };

  const newTask = () => {
    if (!projectId || currentProject?.source === "salesforce") return;
    setTaskWizardOpen(true);
  };

  const openTaskEdit = (task: TaskView) => {
    // SF-Aufgaben oeffnen den read-only-Drawer mit Deep-Link (kein Edit).
    setDrawerTask({
      id: task.id,
      source: task.source,
      sf_url: task.sf_url,
      read_only: task.read_only,
    });
  };

  const deletePhase = async (p: PhaseOut) => {
    const ok = await modal.confirm({
      title: t.projects.workstreamLoeschenFrage(p.name),
      description: t.projects.workstreamLoeschenHinweis,
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/phases/${p.id}`);
      if (phaseFilter === p.id) setPhaseFilter("all");
      await loadPhases(projectId!, "local");
      await loadTasks(projectId!);
    } catch (e) {
      toast.err(t.projects.workstreamLoeschenFehlgeschlagen, (e as Error).message);
    }
  };

  // AC2/AC4: Bestaetigen im Loesch-Modal. Bei 204 aus Liste raus + zur
  // Projektliste navigieren; bei Fehler (403/500) Meldung IM Modal, kein
  // Schliessen, keine Navigation.
  const confirmDeleteProject = async () => {
    if (!currentProject) return;
    setDeleteBusy(true);
    setDeleteErr(null);
    const deletedId = currentProject.id;
    try {
      await api.delete(`/projects/${deletedId}`);
      setProjects((prev) => prev.filter((p) => p.id !== deletedId));
      // Sidebar-Tree ist eine separate Liste mit eigenem State (laedt Projekte
      // nur einmal beim Mount) — ohne Sync bliebe das geloeschte Projekt dort
      // klickbar und wuerde 404en. CustomEvent-Bus (bestehendes Muster im
      // Frontend) haelt beide Listen konsistent.
      window.dispatchEvent(
        new CustomEvent("sb:project-deleted", { detail: { id: deletedId } })
      );
      setDeleteOpen(false);
      toast.ok(t.projects.projektGeloescht);
      navigate("/p");
    } catch (e) {
      setDeleteErr((e as Error).message || t.projects.loeschenFehlgeschlagenPunkt);
    } finally {
      setDeleteBusy(false);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const visibleTasks = useMemo(() => {
    if (phaseFilter === "all") return tasks;
    if (phaseFilter === "none") return tasks.filter((t) => t.phase_id == null);
    return tasks.filter((t) => t.phase_id === phaseFilter);
  }, [tasks, phaseFilter]);

  const grouped: Record<Column, TaskView[]> = { open: [], in_progress: [], done: [] };
  // Tasks-Sortierung: nach Status-Sortierreihenfolge (offen zuerst), dann due_date asc, dann title
  const sortedTasks = [...visibleTasks].sort((a, b) => {
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return a.title.localeCompare(b.title);
  });
  for (const t of sortedTasks) grouped[normalizeStatus(t.status)].push(t);

  const onDragEnd = async (e: DragEndEvent) => {
    setDragging(null);
    const taskId = e.active.id as string;
    const col = e.over?.id as Column | undefined;
    if (!col) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.read_only) return;
    if (normalizeStatus(task.status) === col) return;
    // Optimistisch
    setTasks((s) =>
      s.map((t) => (t.id === taskId ? { ...t, status: col } : t))
    );
    try {
      await api.patch(`/tasks/${taskId}`, { status: col });
    } catch (err) {
      toast.err(t.projects.statusNichtGeaendert, (err as Error).message);
      await loadTasks(projectId!);
    }
  };

  return (
    <div className="h-full">
      {/* Detail — Master-Liste lebt jetzt in der globalen Sidebar (Phase-1, 2026-06-20) */}
      <section className="h-full md:overflow-hidden flex flex-col bg-surface-2">
        {!projectId || !currentProject ? (
          <div className="flex-1 overflow-y-auto flex items-start md:items-center justify-center p-4 text-sm text-text-2">
            <div className="text-center max-w-sm w-full py-6">
              <Folder className="w-10 h-10 text-brand-500 mx-auto mb-2" />
              {/* Desktop: Sidebar ist sichtbar → Verweis darauf. */}
              <div className="hidden md:block font-medium mb-1">
                {t.projects.projektWaehlen}
              </div>
              {/* Mobil: keine Sidebar → Projektliste direkt rendern. */}
              <div className="md:hidden font-medium mb-1">{t.projects.deineProjekte}</div>
              <div className="text-xs">
                {t.projects.eigeneOderNeu(localProjects.length)}
              </div>

              {filteredProjects.length > 0 && (
                <div className="md:hidden mt-4 space-y-1.5 text-left">
                  {filteredProjects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/p/${p.id}`)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-surface border border-border hover:border-brand-400 text-left min-h-11 transition-colors duration-150"
                    >
                      {p.source === "salesforce" && (
                        <Lock className="w-3.5 h-3.5 text-warning shrink-0" />
                      )}
                      <span className="truncate text-sm font-medium text-text">
                        {projectDisplayName(p.name)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={createProject} className="mt-4 flex gap-1 max-w-xs mx-auto">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t.projects.neuesProjektPlatzhalter}
                  className="flex-1 px-2 py-2 md:py-1 text-sm rounded-md bg-surface border border-border focus:outline-none focus:ring-2 focus:ring-brand-500/40 min-h-11 md:min-h-0"
                />
                <button
                  type="submit"
                  className="px-3 py-2 md:px-2 md:py-1 rounded-md bg-brand-600 hover:bg-brand-700 text-white min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center"
                  title={t.common.anlegen}
                  aria-label={t.projects.neuesProjektAnlegen}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        ) : (
          <>
            {/* Phase-2-Layout: zweispaltig — Mode-Switcher + Content links,
                persistente Knowledge-/Instructions-Spalte rechts (300px).
                Bei SF-Fallback (read-only) entfällt die Knowledge-Spalte. */}
            <div
              className={
                currentProject.source === "local"
                  ? "flex-1 min-h-0 flex flex-col md:grid md:grid-cols-[1fr_300px] md:overflow-hidden"
                  : "flex-1 min-h-0 overflow-hidden flex flex-col"
              }
            >
              <div className="flex-1 min-h-0 min-w-0 flex flex-col md:border-r border-border">
                <header className="px-6 py-3 border-b border-border bg-surface flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2">
                    {currentProject.source === "salesforce" && (
                      <Lock className="w-3.5 h-3.5 text-warning shrink-0" />
                    )}
                    <h2 className="font-semibold truncate">{projectDisplayName(currentProject.name)}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Fleet-Board-Switch (Migration 0072): tab-unabhaengig im
                        Header — sichtbar auch fuer frisch onboardete Projekte
                        OHNE Tasks (im Kanban-GoalsPanel erschien er sonst nie).
                        Self-contained-Modus: laedt fleet_enabled selbst aus
                        /projects/{id}/goals (kein neuer Endpoint). */}
                    {currentProject.source === "local" && (
                      <FleetToggle projectId={currentProject.id} variant="inline" />
                    )}
                    {currentProject.source === "salesforce" && currentProject.sf_url && (
                      <a
                        href={currentProject.sf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                      >
                        {t.projects.inSalesforceOeffnen} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {currentProject.source === "local" && activeTab === "tasks" && (
                      <Button onClick={newTask} size="sm" icon={<Plus className="w-3 h-3" />}>
                        {t.projects.aufgabe}
                      </Button>
                    )}
                    {currentProject.source === "local" && (
                      <button
                        onClick={() => setRailOpen(true)}
                        className="md:hidden text-xs border border-border text-text-2 px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                        title={t.projects.kontextTitel}
                      >
                        <Layers className="w-3.5 h-3.5" /> {t.projects.kontext}
                      </button>
                    )}
                    {/* Projekt-Aktionen (nur lokale Projekte; SF ist read-only).
                        Ruhiges MoreVertical-Dropdown analog zum Workstream-Menue,
                        statt eines dauerhaft sichtbaren Danger-Buttons. */}
                    {currentProject.source === "local" && (
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <button
                            className="p-1.5 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
                            aria-label={t.projects.projektAktionen}
                            title={t.projects.projektAktionen}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </Dropdown.Trigger>
                        <Dropdown.Portal>
                          <Dropdown.Content
                            className="bg-surface border border-border rounded-md shadow-lg p-1 min-w-[180px] z-50"
                            align="end"
                            sideOffset={4}
                          >
                            <Dropdown.Item
                              onSelect={() => {
                                setDeleteErr(null);
                                setDeleteOpen(true);
                              }}
                              className="text-sm px-2 py-1.5 rounded-md hover:bg-danger-soft text-danger cursor-pointer inline-flex items-center gap-2 outline-none"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> {t.projects.projektLoeschen}
                            </Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown.Portal>
                      </Dropdown.Root>
                    )}
                  </div>
                </header>

                <ProjectTabs
                  active={activeTab}
                  onChange={setActiveTab}
                  showChats={currentProject.source === "local"}
                  showPresentations={currentProject.source === "local"}
                  showIntake={currentProject.source === "local"}
                  counts={{ tasks: tasks.length, presentations: presentations.length }}
                />

            {activeTab === "chats" && currentProject.source === "local" ? (
              <div className="flex-1 min-h-0">
                <ProjectChatView
                  projectId={currentProject.id}
                  projectName={projectDisplayName(currentProject.name)}
                />
              </div>
            ) : activeTab === "presentations" && currentProject.source === "local" ? (
              <PresentationsTab
                presentations={presentations}
                onNew={() => setPresentationWizardOpen(true)}
                onChanged={() => loadPresentations(currentProject.id)}
              />
            ) : activeTab === "intake" && currentProject.source === "local" ? (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <ProjectIntake projectId={currentProject.id} />
              </div>
            ) : (
              <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
                {currentProject.source === "local" && (
                  <PhasesBar
                    phases={phases}
                    selected={phaseFilter}
                    onSelect={setPhaseFilter}
                    onAddPhase={() => {
                      setEditingPhase(null);
                      setPhaseWizardOpen(true);
                    }}
                    onEditPhase={(p) => {
                      setEditingPhase(p);
                      setPhaseWizardOpen(true);
                    }}
                    onDeletePhase={deletePhase}
                    taskCounts={{
                      all: tasks.length,
                      none: tasks.filter((t) => t.phase_id == null).length,
                      byPhase: Object.fromEntries(
                        phases.map((p) => [p.id, tasks.filter((t) => t.phase_id === p.id).length])
                      ),
                    }}
                  />
                )}
                <div className="flex-1 min-h-0 p-4 overflow-hidden">
                  <DndContext
                    sensors={sensors}
                    onDragStart={(e) =>
                      setDragging(tasks.find((t) => t.id === e.active.id) ?? null)
                    }
                    onDragEnd={onDragEnd}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-[repeat(3,minmax(0,1fr))] gap-4 h-full min-h-[400px]">
                      {(["open", "in_progress", "done"] as Column[]).map((col) => (
                        <KanbanColumn
                          key={col}
                          col={col}
                          tasks={grouped[col]}
                          onAdd={
                            col === "open" && currentProject.source === "local"
                              ? newTask
                              : undefined
                          }
                          onEditTask={openTaskEdit}
                        />
                      ))}
                    </div>
                    <DragOverlay>
                      {dragging ? <TaskCardInner task={dragging} dragging /> : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              </div>
            )}
              </div>
              {currentProject.source === "local" && (
                <>
                  {/* Mobile-Backdrop fuer die Slide-over-Rail */}
                  {railOpen && (
                    <div
                      className="md:hidden fixed inset-0 z-40 bg-black/40"
                      onClick={() => setRailOpen(false)}
                    />
                  )}
                  <aside
                    className={clsx(
                      "p-3 space-y-5 bg-cream-100/60 dark:bg-ink-800/40 overflow-y-auto",
                      // Mobile: Slide-over von rechts, hinter Toggle
                      "fixed top-0 right-0 z-50 h-full w-[85%] max-w-sm border-l border-border shadow-2xl transition-transform",
                      railOpen ? "translate-x-0" : "translate-x-full",
                      // Desktop: normale Grid-Spalte (unveraendert)
                      "md:static md:z-auto md:h-auto md:w-auto md:max-w-none md:border-l-0 md:shadow-none md:translate-x-0 md:min-h-0"
                    )}
                  >
                    <div className="md:hidden flex justify-end -mt-1">
                      <button
                        onClick={() => setRailOpen(false)}
                        className="p-1 text-text-2 hover:text-text"
                        aria-label={t.common.schliessen}
                      >
                        <span className="text-lg leading-none">×</span>
                      </button>
                    </div>
                    <InstructionsPanel projectId={currentProject.id} />
                    <KnowledgePanel projectId={currentProject.id} />
                  </aside>
                </>
              )}
            </div>
          </>
        )}
      </section>

      {projectId && currentProject?.source === "local" && (
        <PresentationWizard
          open={presentationWizardOpen}
          onClose={() => setPresentationWizardOpen(false)}
          projectId={projectId}
          onCreated={() => loadPresentations(projectId)}
        />
      )}

      {projectId && currentProject?.source === "local" && (
        <>
          <PhaseWizard
            open={phaseWizardOpen}
            onClose={() => {
              setPhaseWizardOpen(false);
              setEditingPhase(null);
            }}
            projectId={projectId}
            phase={editingPhase}
            onSaved={async () => {
              await loadPhases(projectId, "local");
              await loadTasks(projectId);
            }}
          />
          {/* TaskWizard nur noch fuer NEUE Aufgaben — Bearbeiten/Antworten
              laeuft ueber den TaskDrawer unten (Karten-Klick). */}
          <TaskWizard
            open={taskWizardOpen}
            onClose={() => setTaskWizardOpen(false)}
            projectId={projectId}
            defaultPhaseId={
              phaseFilter !== "all" && phaseFilter !== "none" ? phaseFilter : null
            }
            phases={phases.map((p) => ({ id: p.id, name: p.name }))}
            onCreated={async () => {
              await loadTasks(projectId);
            }}
          />
          {/* Voller Task-Drawer (Feedback-Banner, Activity, Status, Reviews).
              Karten-Klick oeffnet diesen statt des reinen Edit-Formulars —
              nur so kann der User eine offene ask-Rueckfrage beantworten. */}
          <TaskDrawer
            taskInfo={drawerTask}
            onClose={() => setDrawerTask(null)}
            onChanged={() => loadTasks(projectId)}
          />
        </>
      )}

      {/* Projekt-Loesch-Bestaetigung — dediziertes zentriertes Tailwind-Modal
          (kein window.confirm). Lokaler busy/error-State: bleibt bei Fehler
          offen und zeigt die Meldung im Modal, navigiert nur bei 204. */}
      {currentProject?.source === "local" && (
        <Modal
          open={deleteOpen}
          onClose={() => {
            // Waehrend des Loeschens nicht schliessbar (Backdrop/ESC ignorieren).
            if (deleteBusy) return;
            setDeleteOpen(false);
            setDeleteErr(null);
          }}
          size="sm"
          icon={<Trash2 className="w-4 h-4 text-danger" />}
          title={t.projects.projektLoeschenFrage(currentProject.name)}
          footer={
            <>
              <button
                onClick={() => {
                  if (deleteBusy) return;
                  setDeleteOpen(false);
                  setDeleteErr(null);
                }}
                disabled={deleteBusy}
                className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-3 text-text disabled:opacity-50"
              >
                {t.common.abbrechen}
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={deleteBusy}
                className="px-3 py-1.5 text-sm rounded-md bg-danger hover:bg-danger/90 text-white disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {deleteBusy ? t.projects.loeschenLaeuft : t.projects.endgueltigLoeschen}
              </button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-text-2">
            <p>{t.projects.loeschHinweisDauerhaft}</p>
            <p>
              {t.projects.loeschHinweisAlle}{" "}
              <span className="font-medium text-text">{t.projects.phasenWorkstreams}</span>{" "}
              {t.projects.loeschHinweisRest}
            </p>
            {deleteErr && (
              <div className="bg-danger-soft border border-danger/40 rounded-md px-3 py-2 text-xs text-danger">
                {t.projects.loeschenFehlgeschlagenMit(deleteErr)}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function PhasesBar({
  phases,
  selected,
  onSelect,
  onAddPhase,
  onEditPhase,
  onDeletePhase,
  taskCounts,
}: {
  phases: PhaseOut[];
  selected: PhaseFilter;
  onSelect: (f: PhaseFilter) => void;
  onAddPhase: () => void;
  onEditPhase: (p: PhaseOut) => void;
  onDeletePhase: (p: PhaseOut) => void;
  taskCounts: { all: number; none: number; byPhase: Record<string, number> };
}) {
  const t = useT();
  const tabClass = (active: boolean) =>
    clsx(
      "px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1.5 transition-colors whitespace-nowrap",
      active
        ? "bg-brand-600 text-white"
        : "text-text-2 hover:bg-surface-3"
    );
  return (
    <div className="px-4 py-2 border-b border-border bg-surface/60 flex items-center gap-1.5 overflow-x-auto">
      <Layers className="w-3.5 h-3.5 text-text-3 shrink-0" />
      <button onClick={() => onSelect("all")} className={tabClass(selected === "all")}>
        {t.projects.alle} <span className="opacity-60 text-[10px]">{taskCounts.all}</span>
      </button>
      {phases.map((p) => (
        <div key={p.id} className="flex items-center">
          <button onClick={() => onSelect(p.id)} className={tabClass(selected === p.id)}>
            <span className="truncate max-w-[160px]">{p.name}</span>
            <span className="opacity-60 text-[10px]">{taskCounts.byPhase[p.id] ?? 0}</span>
          </button>
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <button
                className="p-1 text-text-3 hover:text-text -ml-0.5"
                aria-label={t.projects.workstreamAktionen(p.name)}
              >
                <MoreVertical className="w-3 h-3" />
              </button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                className="bg-surface border border-border rounded-md shadow-lg p-1 min-w-[160px] z-50"
                align="start"
                sideOffset={4}
              >
                <Dropdown.Item
                  onSelect={() => onEditPhase(p)}
                  className="text-sm px-2 py-1.5 rounded-md hover:bg-surface-3 cursor-pointer inline-flex items-center gap-2 outline-none"
                >
                  <Pencil className="w-3.5 h-3.5" /> {t.common.bearbeiten}
                </Dropdown.Item>
                <Dropdown.Item
                  onSelect={() => onDeletePhase(p)}
                  className="text-sm px-2 py-1.5 rounded-md hover:bg-danger-soft text-danger cursor-pointer inline-flex items-center gap-2 outline-none"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {t.common.loeschen}
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      ))}
      <button onClick={() => onSelect("none")} className={tabClass(selected === "none")}>
        {t.projects.ohneWorkstream}{" "}
        <span className="opacity-60 text-[10px]">{taskCounts.none}</span>
      </button>
      <div className="flex-1" />
      <button
        onClick={onAddPhase}
        className="px-3 py-1.5 text-xs rounded-md text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 inline-flex items-center gap-1 whitespace-nowrap"
      >
        <Plus className="w-3.5 h-3.5" /> {t.projects.workstream}
      </button>
    </div>
  );
}

function KanbanColumn({
  col,
  tasks,
  onAdd,
  onEditTask,
}: {
  col: Column;
  tasks: TaskView[];
  onAdd?: () => void;
  onEditTask: (t: TaskView) => void;
}) {
  const t = useT();
  const { setNodeRef, isOver } = useDroppable({ id: col });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "rounded-lg border min-h-0 min-w-0 p-2 flex flex-col gap-2 transition-colors",
        COL_COLOR[col],
        isOver && "ring-2 ring-brand-500"
      )}
    >
      <div className="flex items-center gap-2 px-1 pb-1">
        <h3 className="text-sm font-semibold flex-1">{t.projects.spalten[col]}</h3>
        <span className="text-xs text-text-2">{tasks.length}</span>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto">
        {tasks.map((task) => (
          <DraggableTask key={task.id} task={task} onEdit={onEditTask} />
        ))}
        {tasks.length === 0 && (
          <div className="text-center text-[11px] text-text-3 py-6">
            {t.projects.leer}
          </div>
        )}
        {onAdd && (
          <button
            onClick={onAdd}
            className="w-full text-xs text-text-2 hover:text-brand-600 border border-dashed border-border-strong rounded-md py-2 hover:border-brand-500 transition-colors"
          >
            {t.projects.aufgabeHinzufuegen}
          </button>
        )}
      </div>
    </div>
  );
}

function DraggableTask({ task, onEdit }: { task: TaskView; onEdit: (t: TaskView) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: task.read_only,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={clsx(
        "select-none",
        isDragging && "opacity-30",
        task.read_only ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
      )}
    >
      <TaskCardInner task={task} onClick={() => onEdit(task)} />
    </div>
  );
}

function TaskCardInner({
  task,
  dragging,
  onClick,
}: {
  task: TaskView;
  dragging?: boolean;
  onClick?: () => void;
}) {
  const t = useT();
  const isOverdue =
    task.due_date && new Date(task.due_date) < new Date() && normalizeStatus(task.status) !== "done";
  return (
    <div
      onClick={(e) => {
        // Klicks auf eingebettete Links (SF-Deep-Link) sollen den Edit-Wizard
        // NICHT öffnen.
        if ((e.target as HTMLElement).closest("a")) return;
        onClick?.();
      }}
      className={clsx(
        "bg-surface border border-border rounded-md p-2.5 shadow-sm",
        dragging && "shadow-2xl rotate-1",
        task.read_only && "opacity-90",
        onClick && !task.read_only && "hover:border-brand-400 dark:hover:border-brand-500 transition-colors"
      )}
    >
      <div className="flex items-start gap-2">
        {task.read_only && (
          <Lock
            className="w-3 h-3 text-warning shrink-0 mt-0.5"
            aria-label={t.projects.salesforceReadOnly}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-snug line-clamp-2">
            {task.title}
          </div>
          {task.description && task.description.trim() && (
            <div className="mt-1 text-[11.5px] text-text-2 leading-snug line-clamp-2">
              {task.description}
            </div>
          )}
          {(task.due_date || task.start_date) && (
            <div
              className={clsx(
                "mt-1.5 text-[11px] inline-flex items-center gap-1",
                isOverdue
                  ? "text-danger"
                  : "text-text-2"
              )}
            >
              <Calendar className="w-3 h-3" />
              {task.due_date ?? task.start_date}
              {isOverdue && ` · ${t.projects.ueberfaellig}`}
            </div>
          )}
          {/* Eskalation (Decider/Mastermind → needs_human): der User muss eine
              ENTSCHEIDUNG treffen, nicht nur eine A/B-Frage beantworten. Hat
              Vorrang vor dem Frage-Badge — sonst wirkte die Mastermind-
              Eskalation wie eine harmlose Rückfrage. Klick öffnet das
              Entscheidungs-Banner im TaskDrawer. */}
          {task.needs_human ? (
            <div className="mt-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-danger-soft text-danger text-[10px] font-medium px-1.5 py-0.5">
                <AlertCircle className="w-3 h-3 shrink-0" />
                {t.projects.brauchtEntscheidung}
              </span>
            </div>
          ) : (task.status ?? "").toLowerCase() === "feedback" ? (
            /* Feedback (offene Rueckfrage): der User muss antworten — deutlich
               sichtbar machen, sonst wirkt die Karte wie "offen" (normalizeStatus
               faltet feedback in die Offen-Spalte). */
            <div className="mt-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 dark:bg-fuchsia-950/50 text-fuchsia-700 dark:text-fuchsia-300 text-[10px] font-medium px-1.5 py-0.5">
                <HelpCircle className="w-3 h-3 shrink-0" />
                {t.projects.wartetAufAntwort}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {task.sf_url && (
        <a
          href={task.sf_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-brand-600 hover:underline inline-flex items-center gap-0.5 mt-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {t.projects.oeffnen} <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
}

function PresentationsTab({
  presentations,
  onNew,
  onChanged,
}: {
  presentations: PresentationListItem[];
  onNew: () => void;
  onChanged: () => void;
}) {
  const [filter, setFilter] = useState<ArtifactType>("all");
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();
  const modal = useModal();
  const t = useT();
  // Die Filter-Leiste wird pro Render aus dem Katalog gebaut statt aus einer
  // Modul-Konstante: eine Konstante würde die Sprache des ersten Ladens
  // einfrieren.
  const artefaktFilter: { id: ArtifactType; label: string; count?: number }[] = [
    { id: "all", label: t.projects.alle },
    { id: "presentation", label: t.projects.praesentation },
    { id: "blog", label: t.projects.blog },
    { id: "video", label: t.projects.video },
  ];

  const downloadPptx = async (p: PresentationListItem) => {
    const t = (await import("../api")).authToken();
    const url = (await import("../api")).apiUrl(`/presentations/${p.id}/pptx`);
    const r = await fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
    if (!r.ok) return;
    const blob = await r.blob();
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = `${p.title.replace(/[^a-zA-Z0-9-_ ]/g, "_")}.pptx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(dlUrl);
  };

  const removePres = async (p: PresentationListItem) => {
    const ok = await modal.confirm({
      title: t.projects.praesentationLoeschenFrage(p.title),
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    const { api } = await import("../api");
    await api.delete(`/presentations/${p.id}`);
    onChanged();
  };

  const filteredPresentations = filter === "all" || filter === "presentation"
    ? presentations
    : [];

  return (
    <div className="flex-1 overflow-auto p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex bg-surface-3 rounded-md p-0.5 text-xs">
          {artefaktFilter.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={clsx(
                "px-2 py-1 rounded-md transition-colors",
                filter === f.id
                  ? "bg-surface shadow-sm font-medium text-text"
                  : "text-text-2 hover:text-text"
              )}
            >
              {f.label}
              {f.count != null && f.count > 0 && (
                <span className="ml-1 text-[10px] opacity-60">{f.count}</span>
              )}
            </button>
          ))}
        </div>
        <Dropdown.Root open={addOpen} onOpenChange={setAddOpen}>
          <Dropdown.Trigger asChild>
            <Button icon={<Plus className="w-3.5 h-3.5" />}>
              {t.projects.neuesArtefakt}
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[240px] bg-surface border border-border rounded-lg shadow-lg py-1"
            >
              <Dropdown.Item
                onSelect={() => {
                  setAddOpen(false);
                  onNew();
                }}
                className="flex items-start gap-2 px-3 py-2 text-sm hover:bg-surface-3 outline-none cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.projects.praesentation}</div>
                  <div className="text-[11px] text-text-2">
                    {t.projects.praesentationBeschreibung}
                  </div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item
                disabled
                className="flex items-start gap-2 px-3 py-2 text-sm opacity-50 cursor-not-allowed"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.projects.blogArtikel}</div>
                  <div className="text-[11px] text-text-2">
                    {t.projects.blogArtikelBeschreibung}
                  </div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item
                disabled
                className="flex items-start gap-2 px-3 py-2 text-sm opacity-50 cursor-not-allowed"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.projects.heygenVideo}</div>
                  <div className="text-[11px] text-text-2">
                    {t.projects.heygenVideoBeschreibung}
                  </div>
                </div>
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>

      {filteredPresentations.length === 0 && filter !== "blog" && filter !== "video" && (
        <div className="bg-surface border border-border rounded-md p-8 text-center text-sm text-text-2">
          {presentations.length === 0
            ? t.projects.keineArtefakte
            : t.projects.keineTrefferFilter}
        </div>
      )}

      {(filter === "blog" || filter === "video") && (
        <div className="bg-warning-soft border border-warning/30 rounded-md p-4 text-sm text-warning">
          {filter === "blog" ? t.projects.blogAgentFehlt : t.projects.videoAgentFehlt}
        </div>
      )}

      <div className="grid gap-2">
        {filteredPresentations.map((p) => (
          <div
            key={p.id}
            className="bg-surface border border-border rounded-md px-3 py-2.5 flex items-center gap-3"
          >
            <span className="text-xs uppercase tracking-wider text-text-3 font-medium shrink-0">
              PPTX
            </span>
            <button
              type="button"
              onClick={() => navigate(`/pres/${p.id}`)}
              className="flex-1 min-w-0 group text-left"
              title={t.projects.vorschauOeffnen}
            >
              <div className="text-sm font-medium truncate group-hover:text-brand-600">
                {p.title}
              </div>
              <div className="text-[11px] text-text-2 inline-flex items-center gap-2">
                <StatusBadge status={p.status} />
                {p.target_slide_count && <span>{t.projects.slides(p.target_slide_count)}</span>}
                <span>{new Date(p.created_at).toLocaleString("de-DE")}</span>
              </div>
            </button>
            {p.status === "done" && p.pptx_object_key && (
              <button
                type="button"
                onClick={() => navigate(`/pres/${p.id}`)}
                className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> {t.projects.preview}
              </button>
            )}
            {p.status === "done" && p.pptx_object_key && (
              <button
                onClick={() => downloadPptx(p)}
                className="text-xs text-text-2 hover:text-text inline-flex items-center gap-1"
                title={t.projects.pptxHerunterladen}
              >
                ↓
              </button>
            )}
            <button
              onClick={() => removePres(p)}
              className="text-text-3 hover:text-danger p-1"
              title={t.common.loeschen}
            >
              <ExternalLink className="w-3.5 h-3.5 rotate-45" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

type ArtifactType = "all" | "presentation" | "blog" | "video";

function StatusBadge({ status }: { status: string }) {
  const info = statusLabel(status);
  return <UiStatusBadge status={info.status}>{info.label}</UiStatusBadge>;
}
